import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { supabase, randomColor, isConfigured } from '../lib/supabase.js';
import { SPLIT_MODES } from '../lib/splitModes.js';
import { MAX_GROUPS } from '../lib/groupMemory.js';
import { appendActivity, persistActivity, loadActivity, clearActivity } from '../lib/activityFeed.js';

// ─── Auth slice ───────────────────────────────────────────────────────────────
const authSlice = (set) => ({
  user: null,
  authLoading: true,
  authError: null,

  initAuth: async () => {
    // Register listener first so we never miss an auth event
    supabase.auth.onAuthStateChange((_event, s) => set({ user: s?.user ?? null }));

    const { data: { session } } = await supabase.auth.getSession();
    let user = session?.user ?? null;

    // Auto anonymous sign-in for beta: no login screen required
    if (!user && isConfigured()) {
      try {
        // Seed identity from persisted Settings so host doesn't appear as 'Guest'.
        let savedName = '';
        let savedColor = '';
        try {
          const raw = localStorage.getItem('splitsnap-v2');
          if (raw) {
            const persisted = JSON.parse(raw)?.state || {};
            savedName  = (persisted.defaultName  || '').trim();
            savedColor = persisted.defaultColor || '';
          }
        } catch { /* ignore */ }
        const { data, error } = await supabase.auth.signInAnonymously({
          options: {
            data: {
              display_name: savedName || 'Guest',
              avatar_color: savedColor || randomColor(),
            },
          },
        });
        if (error) throw error;
        user = data.user ?? null;
      } catch (err) {
        console.warn('[Auth] Anonymous sign-in failed:', err.message);
        set({ authError: err.message });
      }
    }

    set({ user, authLoading: false });
  },

  setUser:       (user) => set({ user }),
  setAuthError:  (err)  => set({ authError: err }),
  clearAuthError: ()    => set({ authError: null }),
});

// ─── Session slice ────────────────────────────────────────────────────────────
const sessionSlice = (set, get) => ({
  currentSession:  null,
  participants:    [],
  receiptItems:    [],
  claims:          {},
  myParticipant:   null,
  sessionLoading:  false,
  sessionError:    null,
  onlineUsers:     {},
  realtimeStatus:  'connecting', // 'live' | 'reconnecting' | 'offline' | 'connecting'
  lastSyncAt:      null,
  activityFeed:    [],

  setRealtimeStatus: (status) => set({ realtimeStatus: status }),
  setLastSync: (ts = Date.now()) => set({ lastSyncAt: ts }),

  pushActivity: (event) => set((state) => {
    const sid = state.currentSession?.id;
    const next = appendActivity(state.activityFeed, event);
    if (sid) persistActivity(sid, next);
    return { activityFeed: next };
  }),
  hydrateActivity: (sessionId) => set(() => ({ activityFeed: loadActivity(sessionId) })),
  resetActivity: () => set((state) => {
    if (state.currentSession?.id) clearActivity(state.currentSession.id);
    return { activityFeed: [] };
  }),

  setCurrentSession: (session) => set({ currentSession: session }),
  setParticipants:   (p)       => set({ participants: p }),
  setReceiptItems:   (items)   => set({ receiptItems: items }),
  setMyParticipant:  (p)       => set({ myParticipant: p }),

  setOnlineUser:  (uid, data) => set((s) => ({ onlineUsers: { ...s.onlineUsers, [uid]: data } })),
  removeOnlineUser: (uid)     => set((s) => { const n = { ...s.onlineUsers }; delete n[uid]; return { onlineUsers: n }; }),
  setOnlineUsers: (map)       => set({ onlineUsers: map }),

  loadSession: async (sessionId) => {
    set({ sessionLoading: true, sessionError: null });
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select('*, receipt_items(*), participants(*)')
        .eq('id', sessionId)
        .single();
      if (error) throw error;
      set({ currentSession: data, participants: data.participants || [], receiptItems: data.receipt_items || [], sessionLoading: false });
      get().loadClaims(sessionId);
    } catch (err) {
      set({ sessionError: err.message, sessionLoading: false });
    }
  },

  loadClaims: async (sessionId) => {
    const { data } = await supabase
      .from('item_claims')
      .select('item_id, participant_id, claimed_quantity, receipt_items!inner(session_id)')
      .eq('receipt_items.session_id', sessionId);
    if (data) {
      const map = {};
      data.forEach((c) => {
        if (!map[c.item_id]) map[c.item_id] = {};
        map[c.item_id][c.participant_id] = c.claimed_quantity || 1;
      });
      set({ claims: map });
    }
  },

  // Set a participant's claimed quantity directly. qty=0 removes the claim.
  // Used for both single-item toggle (qty 0↔1) and multi-quantity (+/-).
  optimisticClaim: (itemId, participantId, qty = null) => set((state) => {
    const current = state.claims[itemId] || {};
    let nextQty;
    if (qty === null) {
      // Legacy toggle behavior for quantity=1 items
      nextQty = current[participantId] ? 0 : 1;
    } else {
      nextQty = Math.max(0, qty | 0);
    }
    const nextItem = { ...current };
    if (nextQty === 0) delete nextItem[participantId];
    else nextItem[participantId] = nextQty;
    const nextClaims = { ...state.claims };
    if (Object.keys(nextItem).length === 0) delete nextClaims[itemId];
    else nextClaims[itemId] = nextItem;
    return { claims: nextClaims };
  }),

  // Beta debug helper: hard-refresh the current session from the server.
  refreshSession: async () => {
    const { currentSession, loadSession } = get();
    if (!currentSession?.id) throw new Error('No active session');
    await loadSession(currentSession.id);
    const err = get().sessionError;
    if (err) throw new Error(err);
    return true;
  },

  applyClaimEvent: (event, payload) => set((state) => {
    const claims = { ...state.claims };
    const { item_id, participant_id, claimed_quantity } = payload;
    const itemMap = { ...(claims[item_id] || {}) };
    if (event === 'DELETE') {
      delete itemMap[participant_id];
    } else {
      // INSERT or UPDATE
      itemMap[participant_id] = claimed_quantity || 1;
    }
    if (Object.keys(itemMap).length === 0) delete claims[item_id];
    else claims[item_id] = itemMap;
    return { claims };
  }),

  updateParticipant: (participantId, updates) => set((state) => ({
    participants: state.participants.map((p) => p.id === participantId ? { ...p, ...updates } : p),
    myParticipant: state.myParticipant?.id === participantId ? { ...state.myParticipant, ...updates } : state.myParticipant,
  })),

  addParticipant: (participant) => set((s) => ({
    participants: [...s.participants.filter((p) => p.id !== participant.id), participant],
  })),

  clearSession: () => set({
    currentSession: null, participants: [], receiptItems: [],
    claims: {}, myParticipant: null, onlineUsers: {}, activityFeed: [],
  }),
});

// ─── UI slice ─────────────────────────────────────────────────────────────────
const uiSlice = (set) => ({
  activeSessionTab: 'items',
  showQR:           false,
  showExport:       false,
  showSplitMode:    false,
  showTipAdjuster:  false,
  showPaymentSheet: null,   // participantId or null
  scanStage:        'upload',
  scanProgress:     0,
  scanMessage:      '',
  parsedItems:      null,
  parsedReceiptMeta: null,   // { confidence, restaurantName } — set by useScanPipeline
  receiptFile:      null,
  receiptPreviewUrl: null,

  setActiveSessionTab: (tab) => set({ activeSessionTab: tab }),
  setShowQR:           (v)   => set({ showQR: v }),
  setShowExport:       (v)   => set({ showExport: v }),
  setShowSplitMode:    (v)   => set({ showSplitMode: v }),
  setShowTipAdjuster:  (v)   => set({ showTipAdjuster: v }),
  setShowPaymentSheet: (pid) => set({ showPaymentSheet: pid }),
  setScanStage:        (s)   => set({ scanStage: s }),
  setScanProgress:     (p)   => set({ scanProgress: p }),
  setScanMessage:      (m)   => set({ scanMessage: m }),
  setParsedItems:      (i)   => set({ parsedItems: i }),
  setParsedReceiptMeta: (m)  => set({ parsedReceiptMeta: m }),
  setReceiptFile:      (f)   => set({ receiptFile: f }),
  setReceiptPreviewUrl: (u)  => set({ receiptPreviewUrl: u }),
  resetScan: () => set({ scanStage: 'upload', scanProgress: 0, scanMessage: '', parsedItems: null, parsedReceiptMeta: null, receiptFile: null, receiptPreviewUrl: null }),
});

// ─── Settings slice (persisted) ──────────────────────────────────────────────
const settingsSlice = (set) => ({
  tipPct:            18,
  splitMode:         SPLIT_MODES.ITEM,
  splitWeights:      {},      // { [participantId]: number (percentage) }
  defaultName:       '',
  defaultColor:      '#7C3AED',
  pushEnabled:       false,
  hapticsEnabled:    true,
  showCents:         true,
  currency:          'USD',
  venmoHandle:       '',
  cashAppHandle:     '',
  paypalHandle:      '',
  zelleHandle:       '',

  setTipPct:          (pct)  => set({ tipPct: Math.max(0, Math.min(50, pct)) }),
  setSplitMode:       (mode) => set({ splitMode: mode }),
  setSplitWeights:    (w)    => set({ splitWeights: w }),
  setDefaultName:     (n)    => set({ defaultName: n }),
  setDefaultColor:    (c)    => set({ defaultColor: c }),
  setPushEnabled:     (v)    => set({ pushEnabled: v }),
  setHapticsEnabled:  (v)    => set({ hapticsEnabled: v }),
  setShowCents:       (v)    => set({ showCents: v }),
  setCurrency:        (c)    => set({ currency: c }),
  setVenmoHandle:     (h)    => set({ venmoHandle: h }),
  setCashAppHandle:   (h)    => set({ cashAppHandle: h }),
  setPaypalHandle:    (h)    => set({ paypalHandle: h }),
  setZelleHandle:     (h)    => set({ zelleHandle: h }),
});

// ─── Recent & Group Memory slice (persisted) ──────────────────────────────────
const historySlice = (set) => ({
  recentSessions: [],
  savedGroups:    [],
  totalSplit:     0,
  sessionsCount:  0,

  addRecentSession: (session) => set((state) => ({
    recentSessions: [session, ...state.recentSessions.filter((s) => s.id !== session.id)].slice(0, 20),
    sessionsCount:  state.sessionsCount + 1,
    totalSplit:     state.totalSplit + (session.total || 0),
  })),

  removeRecentSession: (id) => set((s) => ({
    recentSessions: s.recentSessions.filter((r) => r.id !== id),
  })),

  addSavedGroup: (group) => set((state) => {
    const existing = state.savedGroups.find((g) => g.id === group.id);
    if (existing) {
      return {
        savedGroups: state.savedGroups.map((g) =>
          g.id === group.id ? { ...g, times_used: g.times_used + 1, last_used: new Date().toISOString() } : g
        ),
      };
    }
    return { savedGroups: [group, ...state.savedGroups].slice(0, MAX_GROUPS) };
  }),

  removeSavedGroup: (id) => set((s) => ({ savedGroups: s.savedGroups.filter((g) => g.id !== id) })),
});

// ─── Combined store ───────────────────────────────────────────────────────────
export const useAppStore = create(
  persist(
    immer((set, get) => ({
      ...authSlice(set, get),
      ...sessionSlice(set, get),
      ...uiSlice(set, get),
      ...settingsSlice(set, get),
      ...historySlice(set, get),
    })),
    {
      name: 'splitsnap-v2',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Settings (always persisted)
        tipPct:         state.tipPct,
        splitMode:      state.splitMode,
        defaultName:    state.defaultName,
        defaultColor:   state.defaultColor,
        pushEnabled:    state.pushEnabled,
        hapticsEnabled: state.hapticsEnabled,
        showCents:      state.showCents,
        currency:       state.currency,
        venmoHandle:    state.venmoHandle,
        cashAppHandle:  state.cashAppHandle,
        paypalHandle:   state.paypalHandle,
        zelleHandle:    state.zelleHandle,
        // History
        recentSessions: state.recentSessions,
        savedGroups:    state.savedGroups,
        totalSplit:     state.totalSplit,
        sessionsCount:  state.sessionsCount,
        // Active session (survives page reload — re-hydrated on next open)
        // Only persist lightweight metadata; full data refetched from Supabase on load
        currentSession:  state.currentSession ? {
          id:       state.currentSession.id,
          name:     state.currentSession.name,
          code:     state.currentSession.code,
          status:   state.currentSession.status,
          tax_rate: state.currentSession.tax_rate,
          tip_pct:  state.currentSession.tip_pct,
        } : null,
        myParticipant: state.myParticipant ? {
          id:           state.myParticipant.id,
          display_name: state.myParticipant.display_name,
          avatar_color: state.myParticipant.avatar_color,
          status:       state.myParticipant.status,
          session_id:   state.myParticipant.session_id,
          user_id:      state.myParticipant.user_id,
        } : null,
      }),
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...persistedState,
        // Never restore full realtime state from storage
        participants: [],
        receiptItems: [],
        claims:       {},
        onlineUsers:  {},
        // Signal that session data needs refetch if we have a session ID
        sessionNeedsRefetch: !!(persistedState.currentSession?.id),
      }),
    }
  )
);

// ─── Typed selectors ──────────────────────────────────────────────────────────
export const sel = {
  user:             (s) => s.user,
  session:          (s) => s.currentSession,
  isHost:           (s) => !!(s.user && s.currentSession && s.currentSession.host_id === s.user.id),
  isGuest:          (s) => !!(s.user && s.currentSession && s.currentSession.host_id && s.currentSession.host_id !== s.user.id),
  participants:     (s) => s.participants,
  items:            (s) => s.receiptItems,
  claims:           (s) => s.claims,
  myParticipant:    (s) => s.myParticipant,
  online:           (s) => s.onlineUsers,
  tipPct:           (s) => s.tipPct,
  splitMode:        (s) => s.splitMode,
  splitWeights:     (s) => s.splitWeights,
  recentSessions:   (s) => s.recentSessions,
  savedGroups:      (s) => s.savedGroups,
  totalSplit:       (s) => s.totalSplit,
  sessionsCount:    (s) => s.sessionsCount,
  scanStage:        (s) => s.scanStage,
  scanProgress:     (s) => s.scanProgress,
  scanMessage:      (s) => s.scanMessage,
  parsedItems:      (s) => s.parsedItems,
  showQR:           (s) => s.showQR,
  showExport:       (s) => s.showExport,
  showSplitMode:    (s) => s.showSplitMode,
  showTipAdjuster:  (s) => s.showTipAdjuster,
  showPaymentSheet: (s) => s.showPaymentSheet,
  activeTab:        (s) => s.activeSessionTab,
  settings:         (s) => ({
    tipPct: s.tipPct, splitMode: s.splitMode, defaultName: s.defaultName,
    defaultColor: s.defaultColor, pushEnabled: s.pushEnabled,
    hapticsEnabled: s.hapticsEnabled, showCents: s.showCents, currency: s.currency,
    venmoHandle: s.venmoHandle, cashAppHandle: s.cashAppHandle, paypalHandle: s.paypalHandle,
    zelleHandle: s.zelleHandle,
  }),
};

// ─── Session reload recovery ──────────────────────────────────────────────────
// Called by SessionScreen on mount when sessionNeedsRefetch is true
export const useSessionReloader = () => {
  const sessionNeedsRefetch = useAppStore((s) => s.sessionNeedsRefetch);
  const currentSession = useAppStore(sel.session);
  const loadSession    = useAppStore((s) => s.loadSession);

  return { sessionNeedsRefetch, sessionId: currentSession?.id, loadSession };
};

// Shallow-equality settings hook — sel.settings returns a fresh object each
// call, which breaks Zustand's Object.is snapshot check and causes
// "Maximum update depth exceeded". Always read settings via this hook.
export const useSettings = () => useAppStore(useShallow(sel.settings));
